import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {
    NatBrowseApplyAccompany,
    NatBrowseApplyTime,
    NatResidentBrowseLocationState,
} from './NatResidentApplicationInterface'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {Box} from '@mui/material'
import {Nullable} from '../../../../app/TypeHelpers'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {Messages} from '../../../../Messages'
import NatResidentBrowseForm from './resident/NatResidentBrowseForm'
import {NatPutBrowseRequest, NatPutBrowseResponse} from '../repository/NatPutBrowse'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location
}

interface State {
    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    browseReasonDetail: Nullable<string>
    videoRecordingPlace: Nullable<string>
    videoRecordingDate: Nullable<string>
    videoRecordingStartTime: Nullable<NatBrowseApplyTime>
    videoRecordingEndTime: Nullable<NatBrowseApplyTime>
    browsePreferredDate: Nullable<string>
    browsePreferredStartTime: Nullable<NatBrowseApplyTime>
    browsePreferredEndTime: Nullable<NatBrowseApplyTime>
    accompanyList: Nullable<NatBrowseApplyAccompany[]>
    note: Nullable<string>
    isLoadCompleted: boolean
}

class NatResidentBrowseApplyInputPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            applicantFamilyName: null,
            applicantFirstName: null,
            browseReasonDetail: null,
            videoRecordingPlace: null,
            videoRecordingDate: null,
            videoRecordingStartTime: null,
            videoRecordingEndTime: null,
            browsePreferredDate: null,
            browsePreferredStartTime: null,
            browsePreferredEndTime: null,
            accompanyList: null,
            note: null,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {condominiumCd, applicationRepo, eventEmitter} = this.props
        const {applyInfo, applyId} = this.props.location.state

        const loginFamilyName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FAMILY_NAME)
        const loginFirstName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FIRST_NAME)
        const defaultApplicantFamilyName = applyInfo?.applicantFamilyName ?? loginFamilyName
        const defaultApplicantFirstName = applyInfo?.applicantFirstName ?? loginFirstName
        let defaultBrowseReasonDetail = applyInfo?.browseReasonDetail ?? null
        let defaultVideoRecordingPlace = applyInfo?.videoRecordingPlace ?? null
        let defaultVideoRecordingDate = applyInfo?.videoRecordingDate ?? null
        let defaultVideoRecordingStartTime = applyInfo?.videoRecordingStartTime ?? null
        let defaultVideoRecordingEndTime = applyInfo?.videoRecordingEndTime ?? null
        let defaultBrowsePreferredDate = applyInfo?.browsePreferredDate ?? null
        let defaultBrowsePreferredStartTime = applyInfo?.browsePreferredStartTime ?? null
        let defaultBrowsePreferredEndTime = applyInfo?.browsePreferredEndTime ?? null
        const defaultAccompanyList = applyInfo?.accompany ?? []
        let defaultNote = applyInfo?.note ?? null

        if (!applyInfo && applyId) {
            await this.props.eventEmitter.showLoading()
            try {
                const browseApplyDetail = await this.props.asyncCallFunction(
                    applicationRepo.getBrowseApplyForResident.bind(
                        applicationRepo, condominiumCd, applyId,
                    ),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (browseApplyDetail && browseApplyDetail.details) {
                    const videoRecordingStartTime = browseApplyDetail.details.videoRecordingStartTime.split(':')
                    const videoRecordingEndTime = browseApplyDetail.details.videoRecordingEndTime.split(':')
                    const browsePreferredStartTime = browseApplyDetail.details.browsePreferredStartTime.split(':')
                    const browsePreferredEndTime = browseApplyDetail.details.browsePreferredEndTime.split(':')

                    defaultBrowseReasonDetail = browseApplyDetail.details.browseReasonDetail
                    defaultVideoRecordingPlace = browseApplyDetail.details.videoRecordingPlace
                    defaultVideoRecordingDate = browseApplyDetail.details.videoRecordingDate
                    defaultVideoRecordingStartTime = {
                        time: browseApplyDetail.details.videoRecordingStartTime,
                        hours: videoRecordingStartTime[0] ?? '',
                        minutes: videoRecordingStartTime[1] ?? '',
                    }
                    defaultVideoRecordingEndTime = {
                        time: browseApplyDetail.details.videoRecordingEndTime,
                        hours: videoRecordingEndTime[0] ?? '',
                        minutes: videoRecordingEndTime[1] ?? '',
                    }
                    defaultBrowsePreferredDate = browseApplyDetail.details.browsePreferredDate
                    defaultBrowsePreferredStartTime = {
                        time: browseApplyDetail.details.browsePreferredStartTime,
                        hours: browsePreferredStartTime[0] ?? '',
                        minutes: browsePreferredStartTime[1] ?? '',
                    }
                    defaultBrowsePreferredEndTime = {
                        time: browseApplyDetail.details.browsePreferredEndTime,
                        hours: browsePreferredEndTime[0] ?? '',
                        minutes: browsePreferredEndTime[1] ?? '',
                    }
                    if (browseApplyDetail.details.accompany) {
                        browseApplyDetail.details.accompany.forEach((accompany, index) => {
                            defaultAccompanyList.push({
                                ...accompany,
                                index: index,
                            })
                        })
                    }
                    defaultNote = browseApplyDetail.details.note
                }
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        }

        this.setState({
            applicantFamilyName: defaultApplicantFamilyName,
            applicantFirstName: defaultApplicantFirstName,
            browseReasonDetail: defaultBrowseReasonDetail,
            videoRecordingPlace: defaultVideoRecordingPlace,
            videoRecordingDate: defaultVideoRecordingDate,
            videoRecordingStartTime: defaultVideoRecordingStartTime,
            videoRecordingEndTime: defaultVideoRecordingEndTime,
            browsePreferredDate: defaultBrowsePreferredDate,
            browsePreferredStartTime: defaultBrowsePreferredStartTime,
            browsePreferredEndTime: defaultBrowsePreferredEndTime,
            accompanyList: defaultAccompanyList,
            note: defaultNote,
            isLoadCompleted: true,
        })
    }

    async onClickApplyConfirmButton(request: NatPutBrowseRequest): Promise<void> {
        const {applicationRepo, condominiumCd} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPutBrowseResponse> = await this.props.asyncCallFunction(
                applicationRepo.applyBrowse.bind(
                    applicationRepo, condominiumCd, request),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {
                    defaultErrorTitle: Messages.EOPCR0014.toString(),
                    defaultErrorTitlePriority: true,
                    replacementDetailErrorMessages: [{
                        targetMessageId: Messages.EOPCA0004,
                        replacementMessageId: Messages.EOPCR0015,
                    }],
                },
            )

            if (response && response.requestError === null) {
                const videoRecordingStartTime = request.videoRecordingStartTime.split(':')
                const videoRecordingEndTime = request.videoRecordingEndTime.split(':')
                const browsePreferredStartTime = request.browsePreferredStartTime.split(':')
                const browsePreferredEndTime = request.browsePreferredEndTime.split(':')

                const locationState: NatResidentBrowseLocationState = {
                    applyInfo: {
                        ...request,
                        videoRecordingStartTime: {
                            time: request.videoRecordingStartTime,
                            hours: videoRecordingStartTime[0] ?? '',
                            minutes: videoRecordingStartTime[1] ?? '',
                        },
                        videoRecordingEndTime: {
                            time: request.videoRecordingEndTime,
                            hours: videoRecordingEndTime[0] ?? '',
                            minutes: videoRecordingEndTime[1] ?? '',
                        },
                        browsePreferredDate: request.browsePreferredDate,
                        browsePreferredStartTime: {
                            time: request.browsePreferredStartTime,
                            hours: browsePreferredStartTime[0] ?? '',
                            minutes: browsePreferredStartTime[1] ?? '',
                        },
                        browsePreferredEndTime: {
                            time: request.browsePreferredEndTime,
                            hours: browsePreferredEndTime[0] ?? '',
                            minutes: browsePreferredEndTime[1] ?? '',
                        },
                        accompany: request.accompany?.filter(accompany =>
                            accompany.accompanyName || accompany.relationship ||
                            accompany.zipCode || accompany.prefecture ||
                            accompany.address2 || accompany.address3 ||
                            accompany.buildingRoomNumber)
                            .map((accompany, index) => {
                                return {
                                    ...accompany,
                                    index: index,
                                }
                            }) ?? null,
                    },
                    applyId: this.props.location.state.applyId,
                }
                this.props.navigate(
                    `/resident/condominiums/${this.props.condominiumCd}/applications/browses/confirm`,
                    {state: JSON.parse(JSON.stringify(locationState))},
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <PlainBackLink
                    to={this.props.location.state.applyId
                        ? `/resident/condominiums/${this.props.condominiumCd}/applications/browses/${this.props.location.state.applyId}/copy`
                        : `/resident/condominiums/${this.props.condominiumCd}/applications/browses/agreement`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    閲覧申請書
                </PageTitle>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatResidentBrowseForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentBrowseApplyInputPage)
